import React, { useState, useEffect, useRef, useMemo, createContext, useContext } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity,
    FlatList,
    ScrollView,
    Dimensions,
    Animated,
    Modal,
} from 'react-native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { useScale, ScaleFn, useDarkMode } from './common';
const maskName = (n: string) => { if (!n || n.length <= 1) return n; if (n.length === 2) return n[0] + "*"; return n[0] + "*".repeat(n.length - 2) + n[n.length - 1]; };
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 컨텍스트 ────────────────────────────────────────
const ThemeContext = createContext<{ dark: boolean; toggle: () => void }>({
    dark: true,
    toggle: () => {},
});
const useTheme = () => useContext(ThemeContext);

// ─── 테마 토큰 ────────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    // 배경: 붉은기를 빼고 아주 연한 웜그레이로 깔아 눈의 피로를 최소화합니다.
    bg:        '#F9FAFB', 
    
    // 카드: 배경보다 명확하게 하얀색을 써서 정보 영역을 돋보이게 합니다.
    card:      '#FFFFFF', 
    
    // 카드 내부: 입력창이나 강조하고 싶은 내부 박스에 사용합니다.
    cardInner: '#F3F4F6', 
    
    // 보더: 현재보다 조금 더 뚜렷하지만 부드러운 회색으로 경계를 잡습니다.
    border:    '#E5E7EB', 
    
    // 텍스트: 완전한 검정보다 눈이 훨씬 편한 짙은 네이비 그레이입니다.
    text:      '#111827', 
    
    // 보조 텍스트: 수치나 단위 설명에 적합한 중간 회색입니다.
    textSub:   '#4B5563', 
    
    // 비활성/뮤트: 중요도가 낮은 정보를 처리할 때 씁니다.
    muted:     '#9CA3AF', 
    
    // 트랙/비활성 배경: 스크롤바나 게이지 배경 등에 사용합니다.
    trackBg:   '#F3F4F6', 
};

type Theme = typeof DARK;

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    diaper:  '#FF6B6B',
    reset:   '#FB923C',
    replace: '#FBBF24',
    noMove:  '#64748B',
    move:    '#3B82F6',
};

const { width: SCREEN_W } = Dimensions.get('window');
const IS_WIDE = SCREEN_W >= 768;

const BLINK_DURATION_MS = 5000;

interface AlarmItem {
    AREA:       string;
    SENSOR_ID:  string;
    NAME?:      string;
    ROOM_NO:    string;
    AMOUNT:     number;
    LAST_VALUE: number;
    VALUE:      number | null;
    ALARM_TYPE: number;
    REGDATE:    string;
}

const ALARM_META: Record<number, { label: string; color: string }> = {
    [-7]: { label: '움직임 감지',      color: S.move    },
    [-6]: { label: '움직임 없음',      color: S.noMove  },
    [-5]: { label: '센서 교체 필요',   color: S.replace },
    [-4]: { label: '센서 초기화 필요', color: S.reset   },
    [-3]: { label: '초기화 알림',      color: S.ok      },
    [0]:  { label: '기저귀 교체',      color: S.diaper  },
};
const getAlarmMeta = (type: number) =>
    ALARM_META[type] ?? { label: '알림', color: S.noMove };

// ─── 집계 헬퍼 ───────────────────────────────────────────
const calcRelay = (list: any[]) => ({
    total:   list.reduce((a, i) => a + (i.REPEATER     ?? 0), 0),
    ok:      list.reduce((a, i) => a + (i.R_CONNECT    ?? 0), 0),
    offline: list.reduce((a, i) => a + (i.R_DISCONNECT ?? 0), 0),
});
const calcSensor = (list: any[]) => ({
    total:   list.reduce((a, i) => a + (i.SENSOR       ?? 0), 0),
    ok:      list.reduce((a, i) => a + (i.S_CONNECT    ?? 0), 0),
    offline: list.reduce((a, i) => a + (i.S_DISCONNECT ?? 0), 0),
});
const calcAlarmCounts = (al: AlarmItem[]) => ({
    move:    al.filter(a => a.ALARM_TYPE === -7).length,
    noMove:  al.filter(a => a.ALARM_TYPE === -6).length,
    replace: al.filter(a => a.ALARM_TYPE === -5).length,
    reset:   al.filter(a => a.ALARM_TYPE === -4).length,
    diaper:  al.filter(a => a.ALARM_TYPE === 0).length,
    total:   al.length,
});

// DETECTION_ST ~ DETECTION_END 시간 범위 체크
const isInDetectionTime = (st: any, end: any): boolean => {
    if (!st || !end) return true;
    const stDate = new Date(st);
    const endDate = new Date(end);
    if (isNaN(stDate.getTime()) || isNaN(endDate.getTime())) return true;
    const stMin  = stDate.getHours() * 60 + stDate.getMinutes();
    const endMin = endDate.getHours() * 60 + endDate.getMinutes();
    const now    = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    if (endMin < stMin) return nowMin >= stMin || nowMin <= endMin;
    if (endMin === stMin) return nowMin === stMin;
    return nowMin >= stMin && nowMin <= endMin;
};

const alarmKey = (a: AlarmItem) => `${a.SENSOR_ID}_${a.ALARM_TYPE}_${a.REGDATE}`;

const getTimeAgo = (regdate: string) => {
    const diffMin = Math.floor((Date.now() - new Date(regdate).getTime()) / 60000);
    if (diffMin < 1)  return '방금 전';
    if (diffMin < 60) return `${diffMin}분 전`;
    const h = Math.floor(diffMin / 60), m = diffMin % 60;
    if (h < 24) return `${h}시간 ${m}분 전`;
    return `${Math.floor(h / 24)}일 전`;
};

// ─── 상태 닷 ────────────────────────────────────────────
const Dot = ({ color, size = 10 }: { color: string; size?: number }) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const dotSt = useMemo(() => makeDotSt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    return (
        <View style={[dotSt.base, {
            width: s(size), height: s(size), borderRadius: s(size / 2),
            backgroundColor: color,
            shadowColor: color,
            shadowRadius: s(size * 0.5),
            borderColor: T.card,
        }]} />
    );
};

// ─── AlarmCard — 깜박임 + NEW 뱃지 ──────────────────────
const AlarmCard = ({ alarm, isNew }: { alarm: AlarmItem; isNew: boolean }) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const feedSt = useMemo(() => makeFeedSt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    const meta      = getAlarmMeta(alarm.ALARM_TYPE);
    const blinkAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        if (!isNew) { blinkAnim.setValue(1); return; }
        // 5초 동안 0.3 ↔ 1 반복 깜박임
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(blinkAnim, { toValue: 0.25, duration: 400, useNativeDriver: true }),
                Animated.timing(blinkAnim, { toValue: 1,    duration: 400, useNativeDriver: true }),
            ])
        );
        loop.start();
        const timer = setTimeout(() => { loop.stop(); blinkAnim.setValue(1); }, BLINK_DURATION_MS);
        return () => { loop.stop(); clearTimeout(timer); };
    }, [isNew]);

    return (
        <Animated.View style={[
            feedSt.item,
            { backgroundColor: T.card, borderLeftColor: meta.color },
            isNew && { opacity: blinkAnim },
        ]}>
            {/* NEW 뱃지 */}
            {isNew && (
                <View style={[feedSt.newBadge, { backgroundColor: meta.color }]}>
                    <Text style={feedSt.newBadgeText}>NEW</Text>
                </View>
            )}
            {/* 컬러 닷 */}
            <View style={[feedSt.dotWrap, { backgroundColor: meta.color + '18' }]}>
                <Dot color={meta.color} size={13} />
            </View>
            <View style={feedSt.itemBody}>
                <View style={feedSt.itemTopRow}>
                    <Text style={[feedSt.itemArea,   { color: T.text }]}>{alarm.AREA}</Text>
                    <Text style={[feedSt.itemRoomNo, { color: T.muted }]}>{alarm.ROOM_NO}</Text>
                    {alarm.NAME ? <Text style={[feedSt.itemName, { color: T.muted }]}>{maskName(alarm.NAME)}</Text> : null}
                </View>
                <Text style={[feedSt.itemMsg, { color: meta.color }]}>{meta.label}</Text>
            </View>
            {/* 수치 뱃지 */}
            {alarm.ALARM_TYPE === 0 && alarm.VALUE != null && (
                <View style={[feedSt.badge, { borderColor: meta.color + '40' }]}>
                    <Text style={[feedSt.badgeText, { color: meta.color }]}>{alarm.VALUE}</Text>
                </View>
            )}
            {/* 시간 뱃지 */}
            <View style={[feedSt.badge, { borderColor: meta.color + '30' }]}>
                <Text style={[feedSt.badgeText, { color: T.muted }]}>{getTimeAgo(alarm.REGDATE)}</Text>
            </View>
        </Animated.View>
    );
};

// ─── AlertFeed — 최대 높이 고정 + 커스텀 스크롤바 ──────────
const FEED_ITEM_H  = 66;
const FEED_MAX_CNT = 3;

const AlertFeed = ({ alarmList, newKeys }: {
    alarmList: AlarmItem[];
    newKeys: Set<string>;
}) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const feedSt = useMemo(() => makeFeedSt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    const scrollY              = useRef(new Animated.Value(0)).current;
    const [contentH, setContentH] = useState(0);
    const [viewH,    setViewH]    = useState(0);

    if (alarmList.length === 0) {
        return (
            <View style={feedSt.empty}>
                <View style={[feedSt.emptyDotWrap, { backgroundColor: S.ok + '20' }]}>
                    <Dot color={S.ok} size={16} />
                </View>
                <Text style={[feedSt.emptyText, { color: T.muted }]}>현재 이상 없음</Text>
            </View>
        );
    }

    const sorted     = [...alarmList].sort((a, b) => new Date(b.REGDATE).getTime() - new Date(a.REGDATE).getTime());
    const maxH       = s(FEED_ITEM_H * FEED_MAX_CNT + 16);
    const needScroll = sorted.length > FEED_MAX_CNT;

    // 썸 높이 · 위치 계산
    const thumbH   = viewH > 0 && contentH > 0 ? Math.max(24, (viewH / contentH) * maxH) : 24;
    const thumbTop = scrollY.interpolate({
        inputRange:  [0, Math.max(contentH - viewH, 1)],
        outputRange: [0, maxH - thumbH],
        extrapolate: 'clamp',
    });

    // 테마별 트랙·썸 색상
    const trackBg = dark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.07)';
    const thumbBg = dark ? 'rgba(148,163,184,0.38)' : 'rgba(71,85,105,0.28)';

    return (
        <View style={feedSt.scrollOuter}>
            <ScrollView
                style={needScroll ? { maxHeight: maxH } : undefined}
                contentContainerStyle={feedSt.wrap}
                nestedScrollEnabled
                showsVerticalScrollIndicator={false}
                scrollEventThrottle={16}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: false }
                )}
                onContentSizeChange={(_, h) => setContentH(h)}
                onLayout={e => setViewH(e.nativeEvent.layout.height)}
            >
                {sorted.slice(0, 15).map((a, i) => (
                    <AlarmCard key={alarmKey(a) + i} alarm={a} isNew={newKeys.has(alarmKey(a))} />
                ))}
            </ScrollView>

            {/* 커스텀 스크롤바 */}
            {needScroll && contentH > viewH && (
                <View style={[feedSt.scrollTrack, { height: maxH, backgroundColor: trackBg }]}>
                    <Animated.View style={[feedSt.scrollThumb, { height: thumbH, backgroundColor: thumbBg, top: thumbTop }]} />
                </View>
            )}
        </View>
    );
};

// ─── PanelHeader ─────────────────────────────────────────
const PanelHeader = ({ title, dotColor, live }: {
    title: string; dotColor?: string; live?: boolean;
}) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const panelSt = useMemo(() => makePanelSt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    return (
        <View style={panelSt.sectionHeader}>
            <Dot color={dotColor ?? S.accent} size={8} />
            <Text style={[panelSt.sectionTitle, { color: T.textSub }]}>{title}</Text>
            {live && (
                <View style={panelSt.liveBadge}>
                    <Dot color={S.err} size={5} />
                    <Text style={panelSt.liveText}>LIVE</Text>
                </View>
            )}
        </View>
    );
};

// ─── StatCard ─────────────────────────────────────────────
const StatCard = ({ label, value, color, T }: {
    label: string; value: number; color: string; T: Theme;
}) => {
    const { scale, s } = useScale();
    const statSt = useMemo(() => makeStatSt(s), [scale]);
    return (
        <View style={[statSt.card, { backgroundColor: T.card, borderColor: value > 0 ? color + '55' : T.border }]}>
            <Dot color={color} size={10} />
            <Text style={[statSt.value, { color }]}>{value}</Text>
            <Text style={[statSt.label, { color: T.muted }]}>{label}</Text>
        </View>
    );
};

// ─── SummarySection ──────────────────────────────────────
const SummarySection = ({ list, alarmCounts }: {
    list: any[];
    alarmCounts: ReturnType<typeof calcAlarmCounts>;
}) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const summarySt = useMemo(() => makeSummarySt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    const relay  = calcRelay(list);
    const sensor = calcSensor(list);
    return (
        <View style={summarySt.wrap}>
            <View style={summarySt.group}>
                <View style={summarySt.groupHeader}>
                    <Ionicons name="radio-outline" size={s(13)} color={S.accent} />
                    <Text style={[summarySt.groupTitle, { color: T.text }]}>중계기</Text>
                    <Text style={[summarySt.groupTotal, { color: T.muted }]}>전체 {relay.total}</Text>
                </View>
                <View style={summarySt.row}>
                    <StatCard label="연결됨"    value={relay.ok}      color={S.ok}  T={T} />
                    <StatCard label="연결 끊김" value={relay.offline} color={S.err} T={T} />
                </View>
            </View>
            <View style={[summarySt.divider, { backgroundColor: T.border }]} />
            <View style={summarySt.group}>
                <View style={summarySt.groupHeader}>
                    <Ionicons name="pulse-outline" size={s(13)} color={S.accent} />
                    <Text style={[summarySt.groupTitle, { color: T.text }]}>센서</Text>
                    <Text style={[summarySt.groupTotal, { color: T.muted }]}>전체 {sensor.total}</Text>
                </View>
                <View style={summarySt.row}>
                    <StatCard label="연결됨"      value={sensor.ok}           color={S.ok}      T={T} />
                    <StatCard label="연결 끊김"   value={sensor.offline}      color={S.err}     T={T} />
                    <StatCard label="기저귀 교체" value={alarmCounts.diaper}  color={S.diaper}  T={T} />
                    <StatCard label="센서 초기화" value={alarmCounts.reset}   color={S.reset}   T={T} />
                    <StatCard label="센서 교체"   value={alarmCounts.replace} color={S.replace} T={T} />
                    <StatCard label="움직임 없음" value={alarmCounts.noMove}  color={S.noMove}  T={T} />
                    <StatCard label="움직임 감지" value={alarmCounts.move}    color={S.move}    T={T} />
                </View>
            </View>
        </View>
    );
};

// ─── RatioSection ────────────────────────────────────────
const MiniRatioBar = ({ total, segments, T }: {
    total: number;
    segments: { value: number; color: string; label: string }[];
    T: Theme;
}) => {
    const { scale, s } = useScale();
    const ratioSt = useMemo(() => makeRatioSt(s), [scale]);
    return (
    <View style={ratioSt.barWrap}>
        <View style={[ratioSt.track, { backgroundColor: T.trackBg }]}>
            {total === 0
                ? <View style={{ flex: 1 }} />
                : segments.map((seg, i) =>
                    seg.value > 0
                        ? <View key={i} style={[ratioSt.seg, { flex: seg.value, backgroundColor: seg.color }]} />
                        : null
                )
            }
        </View>
        <Text style={[ratioSt.totalNum, { color: T.text }]}>
            {total}<Text style={[ratioSt.totalUnit, { color: T.muted }]}> 개</Text>
        </Text>
        <View style={ratioSt.legend}>
            {segments.map(seg => (
                <View key={seg.label} style={ratioSt.legendItem}>
                    <Dot color={seg.color} size={7} />
                    <Text style={[ratioSt.legendLabel, { color: T.textSub }]}>{seg.label}</Text>
                    <Text style={[ratioSt.legendVal, { color: seg.color }]}>{seg.value}</Text>
                </View>
            ))}
        </View>
    </View>
    );
};

const RatioSection = ({ list, alarmCounts }: {
    list: any[];
    alarmCounts: ReturnType<typeof calcAlarmCounts>;
}) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const ratioSt = useMemo(() => makeRatioSt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    const relay  = calcRelay(list);
    const sensor = calcSensor(list);
    return (
        <View style={ratioSt.wrap}>
            <View style={ratioSt.half}>
                <Text style={[ratioSt.subTitle, { color: T.textSub }]}>중계기</Text>
                <MiniRatioBar total={relay.total} T={T} segments={[
                    { value: relay.ok,      color: S.ok,  label: '연결됨' },
                    { value: relay.offline, color: S.err, label: '연결 끊김' },
                ]} />
            </View>
            <View style={[ratioSt.vDivider, { backgroundColor: T.border }]} />
            <View style={ratioSt.half}>
                <Text style={[ratioSt.subTitle, { color: T.textSub }]}>센서</Text>
                <MiniRatioBar total={sensor.total} T={T} segments={[
                    { value: sensor.ok,           color: S.ok,      label: '연결됨' },
                    { value: alarmCounts.diaper,  color: S.diaper,  label: '기저귀 교체' },
                    { value: alarmCounts.reset,   color: S.reset,   label: '센서 초기화' },
                    { value: alarmCounts.replace, color: S.replace, label: '센서 교체' },
                    { value: alarmCounts.noMove,  color: S.noMove,  label: '움직임 없음' },
                    { value: alarmCounts.move,    color: S.move,    label: '움직임 감지' },
                    { value: sensor.offline,      color: S.err,     label: '연결 끊김' },
                ]} />
            </View>
        </View>
    );
};

// ─── ProgressRing (conic-gradient, web) ──────────────────
const ProgressRing = ({ pct, size = 80, thickness = 7, color, T }: {
    pct: number; size?: number; thickness?: number; color: string; T: Theme;
}) => {
    const { s } = useScale();
    const inner = size - thickness * 2;
    const clamp = Math.min(Math.max(pct, 0), 100);
    const deg = clamp * 3.6;
    return (
        <View style={[{
            width: s(size), height: s(size), borderRadius: s(size / 2),
            alignItems: 'center', justifyContent: 'center',
        }, { background: `conic-gradient(${color} ${deg}deg, ${T.trackBg} ${deg}deg)` } as any]}>
            <View style={{
                width: s(inner), height: s(inner), borderRadius: s(inner / 2),
                backgroundColor: T.card, alignItems: 'center', justifyContent: 'center',
            }}>
                <Text style={{ fontSize: s(size * 0.26), fontWeight: '800', color, fontVariant: ['tabular-nums'] as any }}>
                    {clamp}
                </Text>
                <Text style={{ fontSize: s(size * 0.11), fontWeight: '600', color: T.muted, marginTop: s(-2) }}>%</Text>
            </View>
        </View>
    );
};

// ─── HealthOverview (3 ring gauges) ──────────────────────
const HealthOverview = ({ list, alarmCounts }: {
    list: any[]; alarmCounts: ReturnType<typeof calcAlarmCounts>;
}) => {
    const { dark } = useTheme();
    const { s } = useScale();
    const T = dark ? DARK : LIGHT;
    const relay = calcRelay(list);
    const sensor = calcSensor(list);
    const rPct = relay.total > 0 ? Math.round((relay.ok / relay.total) * 100) : 100;
    const sPct = sensor.total > 0 ? Math.round((sensor.ok / sensor.total) * 100) : 100;
    const oPct = (relay.total + sensor.total) > 0
        ? Math.round(((relay.ok + sensor.ok) / (relay.total + sensor.total)) * 100) : 100;
    const gc = (p: number) => p >= 90 ? S.ok : p >= 70 ? S.replace : S.err;

    const items = [
        { label: '중계기 연결률', pct: rPct, sub: `${relay.ok} / ${relay.total}` },
        { label: '센서 연결률', pct: sPct, sub: `${sensor.ok} / ${sensor.total}` },
        { label: '전체 건강도', pct: oPct, sub: `알림 ${alarmCounts.total}건` },
    ];

    return (
        <View style={{ flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center', paddingVertical: s(4) }}>
            {items.map(g => (
                <View key={g.label} style={{ alignItems: 'center', gap: s(10) }}>
                    <ProgressRing pct={g.pct} color={gc(g.pct)} T={T} />
                    <Text style={{ fontSize: s(12), fontWeight: '700', color: T.text }}>{g.label}</Text>
                    <Text style={{ fontSize: s(10), color: T.muted, fontVariant: ['tabular-nums'] as any }}>{g.sub}</Text>
                </View>
            ))}
        </View>
    );
};

// ─── BarLine (mini progress bar) ─────────────────────────
const BarLine = ({ label, ok, total, color, T }: {
    label: string; ok: number; total: number; color: string; T: Theme;
}) => {
    const { s } = useScale();
    const pct = total > 0 ? (ok / total) * 100 : 0;
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(6) }}>
            <Text style={{ fontSize: s(10), color: T.muted, width: s(32) }}>{label}</Text>
            <View style={{ flex: 1, height: s(6), borderRadius: s(3), backgroundColor: T.trackBg, overflow: 'hidden' }}>
                <View style={{ width: `${pct}%` as any, height: '100%', borderRadius: s(3), backgroundColor: color }} />
            </View>
            <Text style={{ fontSize: s(10), color: T.textSub, width: s(30), textAlign: 'right', fontVariant: ['tabular-nums'] as any }}>
                {ok}/{total}
            </Text>
        </View>
    );
};

// ─── AlarmChip (mini alarm indicator) ────────────────────
const AlarmChip = ({ count, color, label }: { count: number; color: string; label: string }) => {
    const { s } = useScale();
    return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(3), backgroundColor: color + '15',
        paddingHorizontal: s(6), paddingVertical: s(2), borderRadius: s(6) }}>
        <View style={{ width: s(5), height: s(5), borderRadius: s(2.5), backgroundColor: color }} />
        <Text style={{ fontSize: s(9), fontWeight: '600', color }}>{label} {count}</Text>
    </View>
    );
};

// ─── AreaStatusGrid (구역별 현황 카드 그리드) ────────────────
const AreaStatusGrid = ({ list, alarmList }: { list: any[]; alarmList: AlarmItem[] }) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const T = dark ? DARK : LIGHT;
    const pct = (n: number, t: number) => (t > 0 ? Math.round((n / t) * 100) : 0);

    // 구역별 집계 + 끊김(중계기+센서) 많은 순 정렬 → 문제 구역이 위로
    const rows = list.map((item: any, i: number) => {
        const rT = item.REPEATER ?? 0, rOk = item.R_CONNECT ?? 0;
        const rBad = item.R_DISCONNECT ?? Math.max(0, rT - rOk);
        const sT = item.SENSOR ?? 0, sOk = item.S_CONNECT ?? 0;
        const sBad = item.S_DISCONNECT ?? Math.max(0, sT - sOk);
        return { area: item.AREA ?? `구역${i + 1}`, rT, rOk, rBad, sT, sOk, sBad };
    }).sort((a, b) => (b.rBad + b.sBad) - (a.rBad + a.sBad));

    const ValCell = ({ n, p, color }: any) => (
        <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={{ fontSize: s(13), fontWeight: '700', color }} numberOfLines={1}>
                {n} <Text style={{ fontSize: s(11), color: T.muted, fontWeight: '500' }}>({p}%)</Text>
            </Text>
        </View>
    );

    return (
        <View style={{ borderWidth: 1, borderColor: T.border, borderRadius: s(10), overflow: 'hidden' }}>
            {/* 헤더 */}
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: T.cardInner, paddingVertical: s(10), paddingHorizontal: s(12), borderBottomWidth: 1, borderBottomColor: T.border, gap: s(6) }}>
                <Text style={{ flex: 1.4, fontSize: s(12), fontWeight: '700', color: T.textSub }}>구역</Text>
                {['중계기 정상', '중계기 끊김', '센서 정상', '센서 끊김'].map(h => (
                    <Text key={h} style={{ flex: 1, fontSize: s(12), fontWeight: '700', color: T.textSub, textAlign: 'center' }} numberOfLines={1}>{h}</Text>
                ))}
            </View>
            {/* 행 */}
            {rows.length === 0 ? (
                <Text style={{ fontSize: s(13), color: T.muted, textAlign: 'center', paddingVertical: s(20) }}>구역이 없습니다.</Text>
            ) : rows.map((r, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: s(10), paddingHorizontal: s(12), gap: s(6), borderTopWidth: i === 0 ? 0 : 1, borderTopColor: T.border }}>
                    <Text style={{ flex: 1.4, fontSize: s(13), fontWeight: '600', color: T.text }} numberOfLines={1}>{r.area}</Text>
                    <ValCell n={r.rOk} p={pct(r.rOk, r.rT)} color={S.ok} />
                    <ValCell n={r.rBad} p={pct(r.rBad, r.rT)} color={r.rBad > 0 ? S.err : T.muted} />
                    <ValCell n={r.sOk} p={pct(r.sOk, r.sT)} color={S.ok} />
                    <ValCell n={r.sBad} p={pct(r.sBad, r.sT)} color={r.sBad > 0 ? S.err : T.muted} />
                </View>
            ))}
        </View>
    );
};

// ─── RightPanel ──────────────────────────────────────────
const RightPanel = ({ list, alarmList, newKeys }: {
    list: any[]; alarmList: AlarmItem[]; newKeys: Set<string>;
}) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const panelSt = useMemo(() => makePanelSt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    const alarmCounts = calcAlarmCounts(alarmList);
    const hasAlarm    = alarmList.length > 0;
    const HR = <View style={[panelSt.divider, { backgroundColor: T.border }]} />;
    return (
        <ScrollView style={[panelSt.panel, { backgroundColor: T.bg }]} showsVerticalScrollIndicator={false}>
            <View style={panelSt.section}>
                <PanelHeader
                    title={`실시간 알림${alarmList.length > 0 ? ` (${alarmList.length})` : ''}`}
                    dotColor={S.diaper}
                    live={hasAlarm}
                />
                <AlertFeed alarmList={alarmList} newKeys={newKeys} />
            </View>
            {HR}
            <View style={panelSt.section}>
                <PanelHeader title="전체 현황" />
                <SummarySection list={list} alarmCounts={alarmCounts} />
            </View>
            {HR}
            <View style={panelSt.section}>
                <PanelHeader title="구역별 현황" />
                <AreaStatusGrid list={list} alarmList={alarmList} />
            </View>
        </ScrollView>
    );
};

// ─── ZoneCard ────────────────────────────────────────────
const ZoneCard = ({ item, alarmList, onPress }: {
    item: any; alarmList: AlarmItem[]; onPress: () => void;
}) => {
    const { dark } = useTheme();
    const { scale, s } = useScale();
    const zoneSt = useMemo(() => makeZoneSt(s), [scale]);
    const T = dark ? DARK : LIGHT;
    const relayIssue   = item.R_DISCONNECT ?? 0;
    const sensorIssue  = item.S_DISCONNECT ?? 0;
    const areaAlarms   = alarmList.filter(a => a.AREA === item.AREA);
    const areaAlarmCnt = areaAlarms.length;
    const hasIssue     = relayIssue > 0 || sensorIssue > 0 || areaAlarmCnt > 0;
    const diaperCnt    = areaAlarms.filter(a => a.ALARM_TYPE === 0).length;
    const resetCnt     = areaAlarms.filter(a => a.ALARM_TYPE === -4).length;
    const replaceCnt   = areaAlarms.filter(a => a.ALARM_TYPE === -5).length;
    const noMoveCnt    = areaAlarms.filter(a => a.ALARM_TYPE === -6).length;
    const moveCnt      = areaAlarms.filter(a => a.ALARM_TYPE === -7).length;

    return (
        <TouchableOpacity
            style={[zoneSt.card, { backgroundColor: T.card, borderColor: T.border, borderLeftColor: hasIssue ? S.err : S.ok }]}
            onPress={onPress}
            activeOpacity={0.85}
        >
            <View style={zoneSt.header}>
                <View style={zoneSt.headerLeft}>
                    <View style={[zoneSt.iconWrap, { backgroundColor: S.accent + '12', borderColor: S.accent + '22' }]}>
                        <Text style={zoneSt.iconText}>🏢</Text>
                    </View>
                    <View>
                        <Text style={[zoneSt.name, { color: T.text }]} numberOfLines={1}>
                            {item.AREA ?? '구역명 없음'}
                        </Text>
                        <Text style={[zoneSt.sub, { color: T.muted }]}>
                            중계기 {item.REPEATER ?? 0}개 · 센서 {item.SENSOR ?? 0}개
                        </Text>
                    </View>
                </View>
                <View style={zoneSt.chipRow}>
                    {areaAlarmCnt > 0 && (
                        <View style={[zoneSt.chip, { backgroundColor: S.diaper + '20' }]}>
                            <Text style={[zoneSt.chipText, { color: S.diaper }]}>알림 {areaAlarmCnt}</Text>
                        </View>
                    )}
                    {(relayIssue + sensorIssue) > 0 && (
                        <View style={[zoneSt.chip, { backgroundColor: S.err + '20' }]}>
                            <Text style={[zoneSt.chipText, { color: S.err }]}>끊김 {relayIssue + sensorIssue}</Text>
                        </View>
                    )}
                </View>
            </View>
            <View style={zoneSt.body}>
                <View style={zoneSt.summaryRow}>
                    <Text style={[zoneSt.summaryLabel, { color: T.textSub }]}>중계기</Text>
                    <View style={zoneSt.summaryRight}>
                        <Text style={{ fontSize: s(12), fontWeight: '600', color: S.ok  }}>연결됨 {item.R_CONNECT    ?? 0}</Text>
                        <Text style={{ fontSize: s(12), fontWeight: '600', color: S.err }}>끊김 {item.R_DISCONNECT ?? 0}</Text>
                    </View>
                </View>
                <View style={zoneSt.summaryRow}>
                    <Text style={[zoneSt.summaryLabel, { color: T.textSub }]}>센서</Text>
                    <View style={zoneSt.summaryRight}>
                        <Text style={{ fontSize: s(12), fontWeight: '600', color: S.ok  }}>연결됨 {item.S_CONNECT    ?? 0}</Text>
                        <Text style={{ fontSize: s(12), fontWeight: '600', color: S.err }}>끊김 {item.S_DISCONNECT ?? 0}</Text>
                    </View>
                </View>
                {areaAlarmCnt > 0 && (
                    <View style={zoneSt.summaryRow}>
                        <Text style={[zoneSt.summaryLabel, { color: T.textSub }]}>알림</Text>
                        <View style={zoneSt.summaryRight}>
                            {diaperCnt  > 0 && <View style={zoneSt.alarmTagRow}><Dot color={S.diaper}  size={8} /><Text style={{ fontSize: s(12), fontWeight: '600', color: S.diaper  }}>{diaperCnt}</Text></View>}
                            {resetCnt   > 0 && <View style={zoneSt.alarmTagRow}><Dot color={S.reset}   size={8} /><Text style={{ fontSize: s(12), fontWeight: '600', color: S.reset   }}>{resetCnt}</Text></View>}
                            {replaceCnt > 0 && <View style={zoneSt.alarmTagRow}><Dot color={S.replace} size={8} /><Text style={{ fontSize: s(12), fontWeight: '600', color: S.replace }}>{replaceCnt}</Text></View>}
                            {noMoveCnt  > 0 && <View style={zoneSt.alarmTagRow}><Dot color={S.noMove}  size={8} /><Text style={{ fontSize: s(12), fontWeight: '600', color: S.noMove  }}>{noMoveCnt}</Text></View>}
                            {moveCnt    > 0 && <View style={zoneSt.alarmTagRow}><Dot color={S.move}    size={8} /><Text style={{ fontSize: s(12), fontWeight: '600', color: S.move    }}>{moveCnt}</Text></View>}
                        </View>
                    </View>
                )}
            </View>
        </TouchableOpacity>
    );
};

// ─── 메인 ────────────────────────────────────────────────
export default function Sensor({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const mainSt = useMemo(() => makeMainSt(s), [scale]);
    const { dark, setDark } = useDarkMode();
    const [list, setList]                 = useState<any[]>([]);
    const [filteredList, setFilteredList] = useState<any[]>([]);
    const [searchText, setSearchText]     = useState('');
    const [alarmList, setAlarmList]       = useState<AlarmItem[]>([]);
    const [newKeys, setNewKeys]           = useState<Set<string>>(new Set());
    const [admin, setAdmin]               = useState('1');
    const [warnings, setWarnings]         = useState<any[]>([]);
    const [warningVisible, setWarningVisible] = useState(false);
    const prevKeysRef                     = useRef<Set<string>>(new Set());
    const newKeyTimers                    = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    // 오프라인·움직임 refs (알림 깜빡임 방지)
    const rawAlarmsRef    = useRef<AlarmItem[]>([]);
    const offlineRef      = useRef<Set<string>>(new Set());
    const movesRef        = useRef<AlarmItem[]>([]);
    // 소변알림 캐시: ALARM_TYPE=0 유지, -1/-3/-6 해제
    const urineCacheRef   = useRef<Map<string, AlarmItem>>(new Map());
    const sensorReady     = useRef(false);

    // 다크모드 저장/복원 (포커스 될 때마다 다시 읽어오기)
    useEffect(() => {
        if (isFocused) getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);
    const toggleDark = () => setDark(!dark);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const toggleFullscreen = () => {
        try {
            const doc = document as any;
            if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
                const el = document.documentElement as any;
                (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
                setIsFullscreen(true);
            } else {
                (doc.exitFullscreen || doc.webkitExitFullscreen)?.call(doc);
                setIsFullscreen(false);
            }
        } catch {}
    };
    useEffect(() => {
        const handler = () => {
            const doc = document as any;
            setIsFullscreen(!!(doc.fullscreenElement || doc.webkitFullscreenElement));
        };
        document.addEventListener('fullscreenchange', handler);
        document.addEventListener('webkitfullscreenchange', handler);
        return () => { document.removeEventListener('fullscreenchange', handler); document.removeEventListener('webkitfullscreenchange', handler); };
    }, []);

    const T = dark ? DARK : LIGHT;

    const updateAlarms = (incoming: AlarmItem[]) => {
        const incomingKeys = new Set(incoming.map(alarmKey));
        const added = [...incomingKeys].filter(k => !prevKeysRef.current.has(k));
        if (added.length > 0) {
            setNewKeys(prev => { const next = new Set(prev); added.forEach(k => next.add(k)); return next; });
            added.forEach(k => {
                if (newKeyTimers.current.has(k)) clearTimeout(newKeyTimers.current.get(k)!);
                const t = setTimeout(() => {
                    setNewKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
                    newKeyTimers.current.delete(k);
                }, BLINK_DURATION_MS);
                newKeyTimers.current.set(k, t);
            });
        }
        prevKeysRef.current = incomingKeys;
        setAlarmList(incoming);
    };

    // 오프라인 필터 + 움직임 + 소변캐시 합쳐서 alarmList에 반영
    const rebuildAlarms = () => {
        if (!sensorReady.current) return;
        const cache = urineCacheRef.current;
        const raw = rawAlarmsRef.current;

        // 알림캐시: 0/-3→시간고정+유지, -1→캐시해제
        raw.forEach(a => {
            if (a.ALARM_TYPE === 0 || a.ALARM_TYPE === -3) {
                // AMOUNT=-2(중계기변경) 행도 캐시에 넣는다. 예전엔 통째로 걸러서, 중계기 두 대에
                // 번갈아 잡히는 센서는 젖어도 알림이 안 떴다 — ALARM_TYPE=0 은 캐시를 통해서만
                // 화면에 올라가고, LAST_VALUE 는 -2 행에서도 정상으로 이어지는데 행째로 버렸다.
                // 단 -2 행의 VALUE 는 예측량이 아니라 원시 커패시턴스(2500대, KIND=0)라 쓰지 않는다.
                const cached = cache.get(a.SENSOR_ID);
                if (!cached || cached.ALARM_TYPE !== a.ALARM_TYPE) {
                    cache.set(a.SENSOR_ID, {
                        ...a,
                        VALUE: a.AMOUNT === -2 ? (cached ? cached.VALUE : null) : a.VALUE,
                    });
                } else {
                    cached.LAST_VALUE = a.LAST_VALUE;
                    if (a.AMOUNT !== -2) cached.VALUE = a.VALUE;
                }
            } else if (a.ALARM_TYPE === -1) {
                cache.delete(a.SENSOR_ID);
            }
        });

        const otherAlarms = raw.filter(a =>
            a.ALARM_TYPE !== -1 && a.ALARM_TYPE !== 0 && a.ALARM_TYPE !== -3
            && !offlineRef.current.has(a.SENSOR_ID));
        const cachedAlarms = [...cache.values()].filter(a => !offlineRef.current.has(a.SENSOR_ID));

        updateAlarms([...otherAlarms, ...cachedAlarms, ...movesRef.current]);
    };

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null;
        const load = async () => {
            const userInfo = await getStorage('AutoLogin');
            if (userInfo?.admin !== undefined) setAdmin(userInfo.admin);
            Data.fetchData('group/getAreaList', {}, (result: any, count: any) => {
                if (count > 0) {
                    const sorted = [...result].sort((a: any, b: any) => {
                        if (a.AREA === '로밍') return 1;
                        if (b.AREA === '로밍') return -1;
                        return a.AREA.localeCompare(b.AREA, 'ko');
                    });
                    setList(sorted);
                }
            }, true);
            Data.fetchData('group/getAlarmList', {}, (result: any, count: any) => {
                rawAlarmsRef.current = count > 0 ? (result as AlarmItem[]) : [];
                rebuildAlarms();
            }, true);
            // 센서 오프라인/움직임 일괄 조회 (1 API)
            Data.fetchData('group/getAllSensorsStatus', {}, (sResult: any, sCount: any) => {
                const offIds = new Set<string>();
                const moves: AlarmItem[] = [];
                if (sCount > 0) {
                    (sResult as any[]).forEach((s: any) => {
                        if (s.CONNECT !== 0) offIds.add(s.SENSOR_ID);
                        if (s.CONNECT === 0 && s.DETECTION_ALARM === 1
                            && isInDetectionTime(s.DETECTION_ST, s.DETECTION_END)
                            && s.POSTURE?.toLowerCase().includes('standup')) {
                            moves.push({
                                AREA: s.AREA, SENSOR_ID: s.SENSOR_ID, NAME: s.NAME,
                                ROOM_NO: s.ROOM_NO || '', AMOUNT: 0, LAST_VALUE: 0, VALUE: 0,
                                ALARM_TYPE: -7, REGDATE: new Date().toISOString(),
                            });
                        }
                        // 움직임 없음: BEDSORE_ALARM 기준(POSTURE_ALARM). 예전엔 getAlarmList 의
                        // AMOUNT=-6 을 썼는데 그게 젖음 알림을 가리고 태블릿과도 달랐다.
                        if (s.CONNECT === 0 && s.POSTURE_ALARM === 1) {
                            moves.push({
                                AREA: s.AREA, SENSOR_ID: s.SENSOR_ID, NAME: s.NAME,
                                ROOM_NO: s.ROOM_NO || '', AMOUNT: 0, LAST_VALUE: 0, VALUE: 0,
                                ALARM_TYPE: -6, REGDATE: new Date().toISOString(),
                            });
                        }
                    });
                }
                sensorReady.current = true;
                offlineRef.current = offIds;
                movesRef.current = moves;
                rebuildAlarms();
            }, true);
        };
        if (isFocused) { load(); interval = setInterval(load, 10000); }
        return () => { if (interval) clearInterval(interval); };
    }, [isFocused]);

    useEffect(() => {
        setFilteredList(
            !searchText ? list
                : list.filter((item: any) =>
                    (item.AREA    ?? '').includes(searchText) ||
                    (item.NAME    ?? '').includes(searchText) ||
                    (item.ROOM_NO ?? '').includes(searchText)
                )
        );
    }, [searchText, list]);

    const relay      = calcRelay(list);
    const sensor     = calcSensor(list);
    const totalAlarm = alarmList.length;

    // WARNING 경고 폴링
    useEffect(() => {
        if (!isFocused) return;
        const checkWarnings = () => {
            Data.fetchData('sensor/getWarnings', {}, (result: any, count: any) => {
                if (count > 0) {
                    setWarnings(result);
                    setWarningVisible(true);
                }
            }, true);
        };
        checkWarnings();
        const wi = setInterval(checkWarnings, 10000);
        return () => clearInterval(wi);
    }, [isFocused]);

    const dismissWarning = (idx: number) => {
        Data.fetchData('sensor/dismissWarning', { IDX: idx }, () => {
            setWarnings(prev => prev.filter(w => w.IDX !== idx));
            if (warnings.length <= 1) setWarningVisible(false);
        }, true);
    };

    const dismissAllWarnings = () => {
        Data.fetchData('sensor/dismissWarning', {}, () => {}, true);
        setWarnings([]);
        setWarningVisible(false);
    };

    return (
        <ThemeContext.Provider value={{ dark, toggle: toggleDark }}>
            <View style={[mainSt.container, { backgroundColor: T.bg }]}>

                {/* WARNING 경고 모달 */}
                <Modal visible={warningVisible} transparent animationType="fade">
                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.7)' }}>
                        <View style={{ backgroundColor: '#1E293B', borderRadius: s(16), padding: s(24), width: '90%', maxWidth: s(500), borderWidth: 2, borderColor: '#F87171' }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: s(16) }}>
                                <Ionicons name="warning" size={s(28)} color="#F87171" />
                                <Text style={{ color: '#F87171', fontSize: s(20), fontWeight: 'bold', marginLeft: s(8) }}>경고 구역 감지</Text>
                            </View>
                            {warnings.map((w, i) => (
                                <View key={w.IDX || i} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: s(10), borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.1)' }}>
                                    <View>
                                        <Text style={{ color: '#E2E8F0', fontSize: s(15), fontWeight: '600' }}>{maskName(w.NAME) || w.SENSOR_ID}</Text>
                                        <Text style={{ color: '#94A3B8', fontSize: s(12), marginTop: s(2) }}>{w.AREA} {w.ROOM_NO} | RSSI: {w.RSSI}</Text>
                                        <Text style={{ color: '#64748B', fontSize: s(11), marginTop: s(2) }}>{w.REGDATE}</Text>
                                    </View>
                                    <TouchableOpacity onPress={() => dismissWarning(w.IDX)} style={{ backgroundColor: '#334155', borderRadius: s(8), paddingHorizontal: s(12), paddingVertical: s(6) }}>
                                        <Text style={{ color: '#94A3B8', fontSize: s(12) }}>확인</Text>
                                    </TouchableOpacity>
                                </View>
                            ))}
                            <TouchableOpacity onPress={dismissAllWarnings} style={{ backgroundColor: '#F87171', borderRadius: s(10), paddingVertical: s(12), marginTop: s(16), alignItems: 'center' }}>
                                <Text style={{ color: '#FFF', fontSize: s(15), fontWeight: 'bold' }}>모두 확인</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Modal>

                {/* ── 상태 인포바 — App.tsx 헤더 아래 얇은 바 ── */}
                <View style={[mainSt.infoBar, { backgroundColor: T.card, borderBottomColor: T.border }]}>
                    {/* 구역 수 */}
                    <Text style={[mainSt.infoBarTitle, { color: T.textSub }]}>{list.length}개 구역</Text>
                    <View style={mainSt.infoBarRight}>
                        {relay.offline > 0 && (
                            <View style={[mainSt.statusChip, { backgroundColor: S.err + '20' }]}>
                                <Ionicons name="radio-outline" size={s(11)} color={S.err} />
                                <Text style={[mainSt.statusChipText, { color: S.err }]}>중계기 끊김 {relay.offline}</Text>
                            </View>
                        )}
                        {sensor.offline > 0 && (
                            <View style={[mainSt.statusChip, { backgroundColor: S.err + '20' }]}>
                                <Ionicons name="pulse-outline" size={s(11)} color={S.err} />
                                <Text style={[mainSt.statusChipText, { color: S.err }]}>센서 끊김 {sensor.offline}</Text>
                            </View>
                        )}
                        {totalAlarm > 0 && (
                            <View style={[mainSt.statusChip, { backgroundColor: S.diaper + '20' }]}>
                                <Dot color={S.diaper} size={6} />
                                <Text style={[mainSt.statusChipText, { color: S.diaper }]}>알림 {totalAlarm}</Text>
                            </View>
                        )}
                        {/* 전체화면 토글 */}
                        <TouchableOpacity
                            onPress={toggleFullscreen}
                            style={[mainSt.iconBtn, { backgroundColor: T.cardInner, borderColor: T.border }]}
                        >
                            <Ionicons name={isFullscreen ? 'contract-outline' : 'expand-outline'} size={s(16)} color={S.accent} />
                        </TouchableOpacity>
                    </View>
                </View>

                {/* ── 본문 ── */}
                <View style={mainSt.body}>
                    <View style={mainSt.leftPane}>
                        <View style={[mainSt.searchWrap, { backgroundColor: T.card, borderColor: T.border }]}>
                            <Ionicons name="search-sharp" size={s(17)} color={T.muted} style={{ marginRight: s(8) }} />
                            <TextInput
                                style={[mainSt.searchInput, { color: T.text }]}
                                placeholder="구역명, 호실 검색..."
                                placeholderTextColor={T.muted}
                                value={searchText}
                                onChangeText={setSearchText}
                            />
                            {searchText.length > 0 && (
                                <TouchableOpacity onPress={() => setSearchText('')}>
                                    <AntDesign name="closecircle" size={s(15)} color={T.muted} />
                                </TouchableOpacity>
                            )}
                        </View>
                        <FlatList
                            data={filteredList}
                            keyExtractor={(item, i) => item.AREA_ID ?? String(i)}
                            contentContainerStyle={mainSt.listContent}
                            showsVerticalScrollIndicator={false}
                            ListEmptyComponent={
                                <View style={mainSt.emptyBox}>
                                    <Ionicons name="cube-outline" size={s(40)} color={T.muted} />
                                    <Text style={[mainSt.emptyText, { color: T.muted }]}>등록된 구역이 없습니다.</Text>
                                </View>
                            }
                            renderItem={({ item }) => (
                                <ZoneCard
                                    item={item}
                                    alarmList={alarmList}
                                    onPress={() => navigation.navigate('SensorScreenDetail', { data: item, area: item.AREA })}
                                />
                            )}
                        />
                    </View>
                    {IS_WIDE && (
                        <View style={[mainSt.rightPane, { borderLeftColor: T.border }]}>
                            <RightPanel list={list} alarmList={alarmList} newKeys={newKeys} />
                        </View>
                    )}
                </View>
            </View>
        </ThemeContext.Provider>
    );
}

// ════════════════════════════════════════════════════════
// StyleSheet
// ════════════════════════════════════════════════════════

const makeDotSt = (s: ScaleFn) => StyleSheet.create({
    base: {
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.75,
        elevation: 3,
        borderWidth: 1.5,
    },
});

const makeMainSt = (s: ScaleFn) => StyleSheet.create({
    container: { flex: 1 },
    statusChip:      { flexDirection: 'row', alignItems: 'center', borderRadius: s(10), paddingHorizontal: s(8), paddingVertical: s(4), gap: s(4) },
    statusChipText:  { fontSize: s(11), fontWeight: '700' },
    iconBtn:         { width: s(34), height: s(34), borderRadius: s(10), justifyContent: 'center', alignItems: 'center', borderWidth: 1 },
    body:            { flex: 1, flexDirection: 'row' },
    leftPane:        { width: IS_WIDE ? s(420) : undefined, flex: IS_WIDE ? undefined : 1 },
    rightPane:       { flex: 1, borderLeftWidth: 1 },
    infoBar:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: s(16), paddingVertical: s(8), borderBottomWidth: 1 },
    infoBarTitle:    { fontSize: s(14), fontWeight: '600', paddingLeft: s(8) },
    infoBarRight:    { flexDirection: 'row', alignItems: 'center', gap: s(6) },
    searchWrap:      { flexDirection: 'row', alignItems: 'center', borderRadius: s(12), margin: s(16), paddingHorizontal: s(14), paddingVertical: s(10), borderWidth: 1 },
    searchInput:     { flex: 1, fontSize: s(14) },
    listContent:     { paddingHorizontal: s(16), paddingBottom: s(32), gap: s(10) },
    emptyBox:        { alignItems: 'center', paddingTop: s(60), gap: s(12) },
    emptyText:       { fontSize: s(14) },
});

const makePanelSt = (s: ScaleFn) => StyleSheet.create({
    panel:         { flex: 1 },
    section:       { padding: s(20) },
    sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: s(8), marginBottom: s(14) },
    sectionTitle:  { fontSize: s(11), fontWeight: '700', letterSpacing: 1.2, textTransform: 'uppercase', flex: 1 },
    divider:       { height: 1, marginHorizontal: s(20) },
    liveBadge:     { flexDirection: 'row', alignItems: 'center', gap: s(4), backgroundColor: 'rgba(248,113,113,0.12)', borderRadius: s(8), paddingHorizontal: s(7), paddingVertical: s(3) },
    liveText:      { fontSize: s(10), color: S.err, fontWeight: '700' },
});

const makeSummarySt = (s: ScaleFn) => StyleSheet.create({
    wrap:        { gap: s(14) },
    group:       { gap: s(10) },
    groupHeader: { flexDirection: 'row', alignItems: 'center', gap: s(6) },
    groupTitle:  { fontSize: s(13), fontWeight: '700', flex: 1 },
    groupTotal:  { fontSize: s(11) },
    row:         { flexDirection: 'row', gap: s(8), flexWrap: 'wrap' },
    divider:     { height: 1 },
});

const makeStatSt = (s: ScaleFn) => StyleSheet.create({
    card:  { flex: 1, minWidth: s(60), borderRadius: s(12), padding: s(10), alignItems: 'center', gap: s(6), borderWidth: 1 },
    value: { fontSize: s(20), fontWeight: '800', fontVariant: ['tabular-nums'] },
    label: { fontSize: s(10), fontWeight: '600', textAlign: 'center' },
});

const makeRatioSt = (s: ScaleFn) => StyleSheet.create({
    wrap:        { flexDirection: 'row' },
    half:        { flex: 1, gap: s(10) },
    subTitle:    { fontSize: s(12), fontWeight: '700', textAlign: 'center' },
    vDivider:    { width: 1, marginHorizontal: s(14) },
    barWrap:     { gap: s(8) },
    track:       { height: s(12), borderRadius: s(6), flexDirection: 'row', overflow: 'hidden' },
    seg:         { height: '100%' },
    totalNum:    { fontSize: s(18), fontWeight: '800', fontVariant: ['tabular-nums'] },
    totalUnit:   { fontSize: s(11), fontWeight: '400' },
    legend:      { gap: s(5) },
    legendItem:  { flexDirection: 'row', alignItems: 'center', gap: s(6) },
    legendLabel: { fontSize: s(11), flex: 1 },
    legendVal:   { fontSize: s(12), fontWeight: '700', fontVariant: ['tabular-nums'] },
});

const makeGridSt = (s: ScaleFn) => StyleSheet.create({
    wrap:       { flexDirection: 'row', flexWrap: 'wrap', gap: s(8) },
    card:       { width: '48%' as any, borderRadius: s(10), padding: s(12), borderWidth: 1, gap: s(8) },
    cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    areaName:   { fontSize: s(12), fontWeight: '700', flex: 1, marginRight: s(6) },
    scoreBadge: { paddingHorizontal: s(7), paddingVertical: s(2), borderRadius: s(6) },
    alarmRow:   { flexDirection: 'row', gap: s(4), flexWrap: 'wrap' },
});

const makeFeedSt = (s: ScaleFn) => StyleSheet.create({
    scrollOuter: { position: 'relative' },
    wrap:        { gap: s(8) },
    // 커스텀 스크롤바 트랙 · 썸
    scrollTrack: {
        position: 'absolute', right: 0, top: 0,
        width: s(3), borderRadius: s(2),
    },
    scrollThumb: {
        position: 'absolute', left: 0, right: 0,
        borderRadius: s(2),
    },
    item: {
        flexDirection: 'row', alignItems: 'center', gap: s(10),
        borderRadius: s(10), padding: s(10), borderLeftWidth: 3,
        overflow: 'hidden',
    },
    newBadge: {
        position: 'absolute', top: 0, right: 0,
        paddingHorizontal: s(6), paddingVertical: s(2),
        borderBottomLeftRadius: s(8),
    },
    newBadgeText: { fontSize: s(9), fontWeight: '800', color: '#fff', letterSpacing: 0.8 },
    dotWrap:      { width: s(32), height: s(32), borderRadius: s(16), alignItems: 'center', justifyContent: 'center' },
    itemBody:     { flex: 1, gap: s(2) },
    itemTopRow:   { flexDirection: 'row', alignItems: 'center', gap: s(6), flexWrap: 'wrap' },
    itemArea:     { fontSize: s(12), fontWeight: '700' },
    itemRoomNo:   { fontSize: s(11) },
    itemName:     { fontSize: s(11) },
    itemMsg:      { fontSize: s(11), fontWeight: '600' },
    badge:        { borderWidth: 1, borderRadius: s(8), paddingHorizontal: s(7), paddingVertical: s(3) },
    badgeText:    { fontSize: s(11), fontWeight: '700', fontVariant: ['tabular-nums'] },
    empty:        { alignItems: 'center', paddingVertical: s(24), gap: s(10) },
    emptyDotWrap: { width: s(44), height: s(44), borderRadius: s(22), alignItems: 'center', justifyContent: 'center' },
    emptyText:    { fontSize: s(13) },
});

const makeZoneSt = (s: ScaleFn) => StyleSheet.create({
    card:         { borderRadius: s(14), borderWidth: 1, borderLeftWidth: 3, overflow: 'hidden' },
    header:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: s(14) },
    headerLeft:   { flexDirection: 'row', alignItems: 'center', flex: 1, gap: s(10) },
    iconWrap:     { width: s(38), height: s(38), borderRadius: s(10), borderWidth: 1, justifyContent: 'center', alignItems: 'center' },
    iconText:     { fontSize: s(17) },
    name:         { fontSize: s(14), fontWeight: '700' },
    sub:          { fontSize: s(11), marginTop: s(2) },
    chipRow:      { flexDirection: 'row', gap: s(4), alignItems: 'center' },
    chip:         { borderRadius: s(10), paddingHorizontal: s(8), paddingVertical: s(4) },
    chipText:     { fontSize: s(11), fontWeight: '700' },
    body:         { paddingHorizontal: s(14), paddingBottom: s(14), gap: s(8) },
    summaryRow:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    summaryLabel: { fontSize: s(12) },
    summaryRight: { flexDirection: 'row', gap: s(10), flexWrap: 'wrap', justifyContent: 'flex-end' },
    alarmTagRow:  { flexDirection: 'row', alignItems: 'center', gap: s(4) },
});
